import { z } from "zod";
import { AgentArch } from "./autopilot.js";
import { AgentRole, roleHas } from "./agent-roles.js";
import { FirewallRule } from "./firewalls.js";
import { OS_RELEASE, osRequirement, RequiresOs } from "./os-support.js";

/*
 * Packages (docs/packages.md, ADR 0002 E): the manifest of a package type,
 * its instances in a project with revisions, conditions and the change
 * journal, and the requests of the packages API. The manifest is
 * declarative: inputs are a JSON Schema subset with widget hints from our
 * fixed set, and every computed value (visibility, validation, price) is a
 * CEL expression (`package-expressions.ts`, a separate entry point so the
 * CEL library loads only where it is used).
 */

/** A package id: also a segment of its routes and of its actions (`pkg.<id>.<action>`). */
export const PACKAGE_ID = /^[a-z][a-z0-9_]{1,31}$/;
export const PackageId = z.string().regex(PACKAGE_ID);
/** An action or input name. */
export const PACKAGE_NAME = /^[a-z][a-z0-9_]{0,31}$/;
const Name = z.string().regex(PACKAGE_NAME);
/** Versions are `major.minor.patch`. */
export const PackageVersion = z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/);

/** A text in both languages of the panel (manifests are never translated by us). */
export const Localized = z.object({
  ru: z.string().trim().min(1).max(600),
  en: z.string().trim().min(1).max(600),
});
export type Localized = z.infer<typeof Localized>;

/** A CEL expression (`package-expressions.ts`): checked when the manifest is loaded. */
export const Expression = z.string().trim().min(1).max(400);

export const PACKAGE_CATEGORIES = [
  "core",
  "network",
  "vpn",
  "apps",
  "monitoring",
  "dns",
  "provider_service",
  "example",
] as const;
export const PackageCategory = z.enum(PACKAGE_CATEGORIES);
export type PackageCategory = z.infer<typeof PackageCategory>;

/** ADR 0002 A: where a package runs. */
export const PACKAGE_PLACEMENTS = [
  "provider",
  "customer-vm",
  "customer-appliance",
  "hybrid",
] as const;
export const PackagePlacement = z.enum(PACKAGE_PLACEMENTS);
export type PackagePlacement = z.infer<typeof PackagePlacement>;

/** ADR 0002 B: who is the source of truth. */
export const OWNERSHIPS = ["managed", "co-managed", "customer"] as const;
export const Ownership = z.enum(OWNERSHIPS);
export type Ownership = z.infer<typeof Ownership>;

/** ADR 0002 C.6: how far Autopilot may act on its own, from looking to acting within policy. */
export const AUTONOMY_LEVELS = ["observe", "suggest", "approve", "policy"] as const;
export const AutonomyLevel = z.enum(AUTONOMY_LEVELS);
export type AutonomyLevel = z.infer<typeof AutonomyLevel>;

/** The risk classes a package action may have (never `admin`: packages do not change who may do what). */
export const PACKAGE_RISKS = ["read", "operate", "change", "create", "destroy", "secret"] as const;
export const PackageRisk = z.enum(PACKAGE_RISKS);
export type PackageRisk = z.infer<typeof PackageRisk>;

/** Our fixed set of input widgets; a manifest cannot bring its own. */
export const INPUT_WIDGETS = [
  "text",
  "secret",
  "number",
  "toggle",
  "select",
  "cidr-list",
  "port",
  "server",
  "network",
  "labels",
  "yaml",
] as const;
export const InputWidget = z.enum(INPUT_WIDGETS);
export type InputWidget = z.infer<typeof InputWidget>;

/** Icons a manifest may name (lucide icons the panel maps); a sanitised SVG comes later. */
export const PACKAGE_ICONS = [
  "package",
  "hand",
  "network",
  "shield",
  "database",
  "cloud",
  "container",
  "globe",
  "activity",
  "key",
] as const;
export const PackageIcon = z.enum(PACKAGE_ICONS);
export type PackageIcon = z.infer<typeof PackageIcon>;

/** Colours only from our tokens. */
export const PACKAGE_TONES = ["neutral", "info", "success", "warning", "danger"] as const;
export const PackageTone = z.enum(PACKAGE_TONES);

export const UI_PLACEMENTS = [
  "server-tab",
  "project-section",
  "dashboard-node",
  "dashboard-badge",
  "dashboard-group",
  "dashboard-edge",
  "create-menu",
] as const;
export const UiPlacement = z.enum(UI_PLACEMENTS);
export type UiPlacement = z.infer<typeof UiPlacement>;

export const STATUS_WIDGETS = ["kv", "table", "log", "chart", "links", "actions"] as const;
export const StatusWidgetKind = z.enum(STATUS_WIDGETS);
export type StatusWidgetKind = z.infer<typeof StatusWidgetKind>;

export const DRIVERS = ["bundle", "compose", "helm", "provider-api", "remote-service"] as const;
export const PackageDriver = z.enum(DRIVERS);

/**
 * How a bundle changes a file that belongs to the customer
 * (docs/packages.md, "Customer files"): `json-merge` sets top-level keys
 * of a JSON object, `line-set` sets `KEY=value` lines. Never written in
 * place without a person's click, unless the file was handed over to the
 * panel (adopted).
 */
export const CUSTOMER_FILE_FORMATS = ["json-merge", "line-set"] as const;
export const CustomerFileFormat = z.enum(CUSTOMER_FILE_FORMATS);
export type CustomerFileFormat = z.infer<typeof CustomerFileFormat>;

/** A customer's file under `/etc` (the bundle loader and the helper refuse system and sudo files). */
export const CustomerFilePath = z
  .string()
  .regex(/^\/etc\/[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+){0,4}$/)
  .refine((p) => !/\/\.\.?(\/|$)/.test(p), "no . or .. segments");

/**
 * A customer's file a package changes, declared in the manifest with why
 * (shown on its card). `when` names a boolean input: the file is changed
 * only while it is on (the bundle's `patch-customer-file … when=<input>`);
 * off, the file is left as it is (`off`). `restart`: the change takes
 * effect only after the bundle's `restart` step (a service reads the file
 * at start); the card offers applying with the restart or without it —
 * `notice` says what the restart does (status keys as in `ui.badge`,
 * `{key|форма|формы|форм}`), `later` when the change takes effect without it.
 */
export const PackageCustomerFile = z.object({
  path: CustomerFilePath,
  format: CustomerFileFormat,
  why: Localized,
  when: Name.optional(),
  restart: z.object({ notice: Localized, later: Localized }).optional(),
});
export type PackageCustomerFile = z.infer<typeof PackageCustomerFile>;
export type PackageDriver = z.infer<typeof PackageDriver>;

const InputValueSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]);
/** A value of one input: text, number, toggle or a list of texts. */
export const InputValue = InputValueSchema;
export type InputValue = z.infer<typeof InputValue>;
export const InputValues = z.record(Name, InputValue);
export type InputValues = z.infer<typeof InputValues>;

/** One input: a JSON Schema subset plus our `x-ui` hints. */
export const InputProperty = z
  .object({
    type: z.enum(["string", "number", "integer", "boolean", "array"]),
    title: Localized,
    description: Localized.optional(),
    default: InputValueSchema.optional(),
    enum: z.array(z.string().max(100)).min(1).max(50).optional(),
    minLength: z.number().int().min(0).max(4096).optional(),
    maxLength: z.number().int().min(1).max(4096).optional(),
    /** An ECMAScript regular expression the whole value must match (anchored by us). */
    pattern: z.string().min(1).max(200).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    items: z
      .object({ type: z.literal("string"), pattern: z.string().max(200).optional() })
      .optional(),
    maxItems: z.number().int().min(1).max(100).optional(),
    "x-ui": z.object({
      widget: InputWidget,
      /** Shown only while this is true (CEL over `inputs`); hidden inputs are neither checked nor sent. */
      visibleWhen: Expression.optional(),
      /** Labels of `enum` values for a select. */
      options: z.record(z.string(), Localized).optional(),
      /**
       * A select: one line per `enum` value saying what it means; the select
       * is then shown as a choice of cards (for a few values, such as a mode).
       */
      optionHints: z.record(z.string(), Localized).optional(),
      placeholder: Localized.optional(),
      /**
       * Left empty, the API fills it from the server the package goes on:
       * `server-name` is the server's name as a host name (lower case,
       * letters, digits and dashes).
       */
      defaultFrom: z.enum(["server-name"]).optional(),
      /** `cidr-list`: offer the ranges of the project's private networks to add. */
      networks: z.boolean().optional(),
    }),
  })
  .strict();
export type InputProperty = z.infer<typeof InputProperty>;

export const InputsSchema = z
  .object({
    type: z.literal("object"),
    properties: z.record(Name, InputProperty).default({}),
    required: z.array(Name).default([]),
    /** Rules across inputs (CEL over `inputs`), each with its message. */
    "x-validate": z
      .array(z.object({ rule: Expression, message: Localized, input: Name.optional() }))
      .max(20)
      .default([]),
  })
  .strict();
export type InputsSchema = z.infer<typeof InputsSchema>;

/** The connect providers the API knows (`apps/api/src/packages/connect/`). */
export const CONNECT_PROVIDERS = ["tailscale"] as const;
export const ConnectProvider = z.enum(CONNECT_PROVIDERS);
export type ConnectProvider = z.infer<typeof ConnectProvider>;

/**
 * One way to connect a package to a third party (docs/packages.md,
 * "Connect flows"); the core does the work, the panel renders each.
 */
export const PackageConnectMethod = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  /**
   * A secret the person pastes (an auth key, a token): one `secret` input,
   * stored encrypted, sent to the server only on the helper's standard input.
   */
  z.object({
    kind: z.literal("secret"),
    input: Name,
    title: Localized,
    help: Localized,
  }),
  /**
   * Client credentials the organisation keeps as a reusable connection
   * (stored encrypted); the API mints a short-lived credential per install
   * through the provider's API and puts it in the `secret` input `input`.
   * `requiredInputs` must hold a value with this method (the provider needs
   * them to mint, e.g. tags).
   */
  z.object({
    kind: z.literal("oauth"),
    provider: ConnectProvider,
    scopes: z.array(z.string().max(100)).max(20).default([]),
    input: Name,
    requiredInputs: z.array(Name).max(5).default([]),
    title: Localized,
    help: Localized,
  }),
  /**
   * The server signs in without a credential: the `operation` action
   * `action` prints `{"url": …}`, a link the person opens to sign in there;
   * the panel polls the status until `status.key` holds one of `status.done`,
   * and gives up after `timeoutSeconds`.
   */
  z.object({
    kind: z.literal("device-login"),
    action: Name,
    status: z.object({ key: Name, done: z.array(z.string().max(32)).min(1).max(5) }),
    pollSeconds: z.number().int().min(2).max(60).default(5),
    timeoutSeconds: z.number().int().min(60).max(1800).default(600),
    title: Localized,
    help: Localized,
  }),
]);
export type PackageConnectMethod = z.infer<typeof PackageConnectMethod>;
export type ConnectKind = PackageConnectMethod["kind"];

export const PackageConnect = z.union([
  PackageConnectMethod,
  /**
   * The person chooses one of several methods in the wizard. `reconnect`:
   * an `operation` action that connects again (a secret parameter named
   * like the methods' input takes a new key; with `oauth` the API mints it).
   */
  z.object({
    kind: z.literal("choice"),
    methods: z.array(PackageConnectMethod).min(2).max(3),
    reconnect: Name.optional(),
  }),
]);
export type PackageConnect = z.infer<typeof PackageConnect>;

/** The methods a manifest allows, in its order (`none` for a package without one). */
export function connectMethods(connect: PackageConnect): PackageConnectMethod[] {
  return connect.kind === "choice" ? connect.methods : [connect];
}

/** The input a method fills with a credential (`secret`, `oauth`), or null. */
export function connectInput(method: PackageConnectMethod): string | null {
  return method.kind === "secret" || method.kind === "oauth" ? method.input : null;
}

/**
 * A package that runs in several ways (ADR 0002 A): the select input
 * `input` chooses one, and each value says where it runs and who owns it
 * (Tailscale: a client on the customer's VM, co-managed; a subnet router
 * as a managed appliance). The top-level `placement` and `ownership` are
 * those of the input's default.
 */
export const PackageModes = z.object({
  input: Name,
  values: z.record(
    Name,
    z.object({
      placement: PackagePlacement,
      ownership: z.object({ default: Ownership, allowed: z.array(Ownership).min(1) }),
    }),
  ),
});
export type PackageModes = z.infer<typeof PackageModes>;

export const PackageOutput = z.object({
  name: Name,
  kind: z.enum(["endpoint", "credential", "ip", "text"]),
  title: Localized,
  /** The key of the status JSON the value comes from. */
  from: Name.optional(),
});
export type PackageOutput = z.infer<typeof PackageOutput>;

/** A value of a resource's spec: written in the manifest, or the value of one of its inputs. */
export const ResourceBinding = z.union([
  z.string().trim().min(1).max(64),
  z.object({ input: Name }).strict(),
]);
export type ResourceBinding = z.infer<typeof ResourceBinding>;

/**
 * A server a package creates in the customer's project (docs/packages.md,
 * "Resources"): a managed appliance (ADR 0002 A). The executor plans it
 * from the catalogue, prices it, creates it through the server-create path
 * with the labels `cloudserver-managed=true`, `cloudserver-package`,
 * `cloudserver-instance`, Autopilot (role `agent-apps`) in its cloud-init
 * and no customer SSH key, turns delete and rebuild protection on, waits
 * for the helper, and runs the bundle on the first server resource.
 */
export const ServerResourceSpec = z
  .object({
    type: z.literal("server"),
    /** The resource's key in the instance (`router`): its name and its bundle values (`r_<key>_…`). */
    key: z.string().regex(/^[a-z][a-z0-9]{0,11}$/),
    title: Localized,
    /** A server type code of the catalogue (`cx23`), or an input that holds one. */
    serverType: ResourceBinding,
    /** A catalogue image code (`ubuntu-24.04`), or an input that holds one. */
    image: ResourceBinding,
    /**
     * Where it runs: a location code (`fsn1`), an input that holds one, or
     * `{network: <input>}` — the first location (catalogue order) of the
     * network zone of the network that input names which offers the type.
     */
    location: z.union([ResourceBinding, z.object({ network: Name }).strict()]),
    /**
     * An input whose private networks the server joins: widget `network`
     * (one id or a list), or `cidr-list` — the project's networks whose
     * range holds one of its CIDRs (a router joins what it advertises).
     * A change of the input attaches or detaches networks before the apply.
     */
    networks: z.object({ input: Name }).strict().optional(),
    /**
     * Routes the server is the gateway of in each network it joins
     * (`100.64.0.0/10` → its private IP: a subnet router's return path),
     * added once it is in the network and removed before it leaves.
     */
    networkRoutes: z
      .array(z.string().regex(/^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/))
      .max(5)
      .default([]),
    /** The server exists only while this holds (CEL over `inputs`): one mode of the package. */
    when: Expression.optional(),
    /** A firewall made for the server with these rules (inbound and outbound), deleted with it. */
    firewall: z
      .object({ rules: z.array(FirewallRule).min(1).max(20) })
      .strict()
      .optional(),
    /** Labels beside ours (`cloudserver-*` keys are ours and refused here). */
    labels: z.record(z.string().regex(/^[a-z][a-z0-9._-]{0,40}$/), z.string().max(63)).default({}),
    publicIpv4: z.boolean().default(true),
    publicIpv6: z.boolean().default(true),
  })
  .strict();
export type ServerResourceSpec = z.infer<typeof ServerResourceSpec>;

/** Resources of later stages: validated and shown, not created yet. */
export const OtherResourceSpec = z.object({
  type: z.enum(["volume", "floating_ip", "load_balancer", "network", "plane_service"]),
  title: Localized,
  /** Monthly price in EUR the resource adds (CEL over `inputs`), when not priced from the catalogue. */
  monthly: Expression.optional(),
  /** The resource exists only while this holds (CEL over `inputs`), e.g. one mode of the package. */
  when: Expression.optional(),
});

export const PackageResource = z.union([ServerResourceSpec, OtherResourceSpec]);
export type PackageResource = z.infer<typeof PackageResource>;

/** The labels every managed appliance carries (ADR 0002 A); drift when one goes. */
export const MANAGED_LABEL = "cloudserver-managed";
export const PACKAGE_LABEL = "cloudserver-package";
export const INSTANCE_LABEL = "cloudserver-instance";

/**
 * The bundle values an appliance package's templates and scripts may
 * declare as `param r_<key>_<field>` (docs/packages.md "Resources"): the
 * executor fills them from what was created. `network_ranges` and
 * `subnets` are the attached networks' IP ranges and cloud subnets,
 * comma-separated CIDRs; `private_ips` the server's addresses in them.
 */
export const RESOURCE_VALUE_FIELDS = [
  "name",
  "location",
  "zone",
  "ipv4",
  "ipv6",
  "private_ips",
  "network_ranges",
  "subnets",
] as const;
export type ResourceValueField = (typeof RESOURCE_VALUE_FIELDS)[number];

/** The reserved parameter of an appliance bundle's `action:take_over` script: the person's key. */
export const TAKE_OVER_KEY_PARAM = "ssh_key";
export const TAKE_OVER_ACTION = "take_over";

/** Whether a package (or one of its modes) runs as a managed appliance it creates. */
export const isAppliancePackage = (manifest: {
  placement: PackagePlacement;
  modes?: { values: Record<string, { placement: PackagePlacement }> } | undefined;
}): boolean =>
  manifest.placement === "customer-appliance" ||
  Object.values(manifest.modes?.values ?? {}).some((m) => m.placement === "customer-appliance");

/** Server resources of a manifest (the ones the executor creates). */
export const serverResources = (manifest: { resources: PackageResource[] }) =>
  manifest.resources.filter((r): r is ServerResourceSpec => r.type === "server");

/** Day-2 actions: what they change, as a new revision or as a check. */
export const ActionEffect = z.discriminatedUnion("kind", [
  /** New inputs (CEL over `inputs` and `params` per input), then a new revision applied. */
  z.object({ kind: z.literal("reconfigure"), set: z.record(Name, Expression) }),
  /** Reads the status and drift again; changes nothing. */
  z.object({ kind: z.literal("check") }),
  /**
   * Runs the bundle's `action:<id>` script on the server (a restart, a
   * prune, reading logs): no new revision, the desired state stays as it
   * is; what the script prints comes back as the answer's `output`.
   */
  z.object({ kind: z.literal("operation") }),
]);
export type ActionEffect = z.infer<typeof ActionEffect>;

export const PackageAction = z.object({
  id: Name,
  title: Localized,
  description: Localized,
  risk: PackageRisk,
  params: InputsSchema.default({ type: "object", properties: {}, required: [], "x-validate": [] }),
  costImpact: z.enum(["none", "increase", "decrease"]).default("none"),
  confirmation: z.enum(["none", "confirm", "type-name"]).default("confirm"),
  effect: ActionEffect,
});
export type PackageAction = z.infer<typeof PackageAction>;

export const StatusWidget = z.object({
  widget: StatusWidgetKind,
  title: Localized,
  /** Top-level key of the instance's status JSON the widget shows (`actions`: none). */
  source: Name.optional(),
  /**
   * `kv`: the keys to show with their labels; `table`: the columns. `values`
   * names a field's known values in both languages (`running` → «работает»).
   */
  fields: z
    .array(
      z.object({
        key: Name,
        label: Localized,
        values: z.record(z.string().max(32), Localized).optional(),
      }),
    )
    .max(20)
    .optional(),
  /** `actions`: the ids of the day-2 actions to offer as buttons. */
  actions: z.array(Name).max(10).optional(),
});
export type StatusWidget = z.infer<typeof StatusWidget>;

export const PackageManifest = z
  .object({
    id: PackageId,
    version: PackageVersion,
    category: PackageCategory,
    publisher: z.object({ name: z.string().min(1).max(100), firstParty: z.boolean() }),
    /** How the bundle is signed; the signature itself sits next to the bundle (docs/packages.md). */
    signature: z.object({
      scheme: z.literal("sshsig-ed25519"),
      signer: z.string().regex(/^[a-z][a-z0-9-]{1,63}$/),
      namespace: z.literal("cloudserver-bundle"),
    }),
    licence: z.string().min(1).max(64),
    placement: PackagePlacement,
    ownership: z.object({ default: Ownership, allowed: z.array(Ownership).min(1) }),
    modes: PackageModes.optional(),
    requires: z
      .object({
        /** Distributions, or the lowest version and the vendor's repository per distribution. */
        os: RequiresOs.optional(),
        arch: z.array(AgentArch).optional(),
        autopilotRole: AgentRole.optional(),
        /** Releases our scripts were verified on (`ubuntu-24.04`); others the vendor serves get a note. */
        tested: z.array(z.string().regex(OS_RELEASE)).max(20).optional(),
        packages: z.array(PackageId).default([]),
      })
      .default({ packages: [] }),
    conflicts: z.array(PackageId).default([]),
    connect: PackageConnect.default({ kind: "none" }),
    inputs: InputsSchema,
    outputs: z.array(PackageOutput).max(20).default([]),
    resources: z.array(PackageResource).max(20).default([]),
    lifecycle: z.object({
      driver: PackageDriver,
      steps: z.array(
        z.enum([
          "install",
          "configure",
          "upgrade",
          "backup",
          "uninstall",
          "health",
          "drift",
          "rollback",
        ]),
      ),
      uninstall: z.object({ data: z.enum(["keep", "delete", "ask"]) }),
      timeoutSeconds: z.number().int().min(10).max(1800).default(300),
      /** The customer's files the bundle changes (`patch-customer-file`), one line each. */
      customerFiles: z.array(PackageCustomerFile).max(8).default([]),
    }),
    actions: z.array(PackageAction).max(30).default([]),
    observe: z
      .object({
        metrics: z
          .array(z.object({ id: Name, title: Localized, unit: z.string().max(16), from: Name }))
          .default([]),
        logs: z.array(z.object({ title: Localized, path: z.string().max(200) })).default([]),
        alerts: z
          .array(
            z.object({
              metric: Name,
              op: z.enum([">", ">=", "<", "<="]),
              threshold: z.number(),
              severity: z.enum(["warning", "critical"]),
            }),
          )
          .default([]),
        siem: z.array(z.object({ id: Name, title: Localized })).default([]),
      })
      .default({ metrics: [], logs: [], alerts: [], siem: [] }),
    ui: z.object({
      icon: PackageIcon,
      tone: PackageTone.default("neutral"),
      placements: z.array(UiPlacement).min(1),
      texts: z.object({
        name: Localized,
        summary: Localized,
        description: Localized,
        /** What removing it does beyond its files (shown in the remove dialog). */
        remove: Localized.optional(),
      }),
      /**
       * The dashboard badge's text (placement `dashboard-badge`) from the
       * status JSON: `{key}` is a value, `{key|form|form…}` a number with its
       * word in the language's plural forms (ru: one, few, many; en: one,
       * other). Without it, or while a key has no value, the badge is the name.
       */
      badge: z.object({ text: Localized }).optional(),
      status: z.array(StatusWidget).max(12).default([]),
      wizard: z
        .array(z.object({ id: Name, title: Localized, inputs: z.array(Name).min(1) }))
        .max(8)
        .default([]),
      /** First-party packages only: a reviewed React component keyed by package id. */
      component: z.boolean().default(false),
    }),
    ai: z.object({
      /** For the model, in English. */
      description: z.string().min(1).max(2000),
      runbooks: z
        .array(
          z.object({
            id: Name,
            title: z.string().max(200),
            steps: z.array(z.string().max(400)).max(20),
          }),
        )
        .max(10)
        .default([]),
      /** Owner's decision 3 of 2026-10-22: act after one click on the card. */
      autonomyDefault: AutonomyLevel.default("approve"),
    }),
    billing: z.object({
      model: z.enum(["monthly", "passthrough", "meter"]),
      /** EUR per month (CEL over `inputs`); packages have no prices yet, usage is recorded. */
      monthly: Expression.default("0.0"),
      meters: z.array(z.object({ id: Name, unit: z.string().max(16) })).default([]),
    }),
  })
  .strict()
  .superRefine((m, ctx) => {
    const issue = (message: string, path: (string | number)[]) =>
      ctx.addIssue({ code: "custom", message, path });
    const inputs = m.inputs.properties;
    for (const name of m.inputs.required)
      if (!inputs[name]) issue(`required input ${name} is not declared`, ["inputs", "required"]);
    for (const release of m.requires.tested ?? [])
      if (osRequirement(m.requires.os, release).kind === "unsupported")
        issue(`tested release ${release} is not in requires.os`, ["requires", "tested"]);
    for (const [name, p] of Object.entries(inputs)) {
      if (p.enum && p["x-ui"].widget === "select") {
        for (const value of p.enum)
          if (!p["x-ui"].options?.[value])
            issue(`select ${name} has no label for ${value}`, ["inputs", name]);
      }
      if (p.pattern) {
        try {
          new RegExp(p.pattern, "u");
        } catch {
          issue(`input ${name} has an invalid pattern`, ["inputs", name, "pattern"]);
        }
      }
    }
    const ids = m.actions.map((a) => a.id);
    const methods = connectMethods(m.connect);
    const kinds = methods.map((method) => method.kind);
    if (new Set(kinds).size !== kinds.length) issue("connect methods must differ", ["connect"]);
    if (m.connect.kind === "choice" && kinds.includes("none"))
      issue("a choice of connect methods has no `none`", ["connect"]);
    const operation = (id: string) =>
      m.actions.find((a) => a.id === id)?.effect.kind === "operation";
    for (const method of methods) {
      const input = connectInput(method);
      if (input !== null && inputs[input]?.["x-ui"].widget !== "secret")
        issue("connect.input must name a secret input", ["connect", "input"]);
      if (method.kind === "oauth")
        for (const name of method.requiredInputs)
          if (!inputs[name]) issue(`connect requires unknown input ${name}`, ["connect"]);
      if (method.kind === "device-login" && !operation(method.action))
        issue("device-login.action must name an operation action", ["connect", "action"]);
    }
    if (m.connect.kind === "choice" && m.connect.reconnect && !operation(m.connect.reconnect))
      issue("connect.reconnect must name an operation action", ["connect", "reconnect"]);
    const connectInputs = new Set(methods.flatMap((method) => connectInput(method) ?? []));
    if (new Set(ids).size !== ids.length) issue("action ids must be unique", ["actions"]);
    for (const reserved of ["install", "configure", "remove"])
      if (ids.includes(reserved)) issue(`action id ${reserved} is reserved`, ["actions"]);
    for (const action of m.actions)
      if (action.effect.kind === "reconfigure")
        for (const name of Object.keys(action.effect.set))
          if (!inputs[name]) issue(`action ${action.id} sets unknown input ${name}`, ["actions"]);
    const wizard = new Set(m.ui.wizard.flatMap((s) => s.inputs));
    for (const name of wizard)
      if (!inputs[name]) issue(`wizard names unknown input ${name}`, ["ui", "wizard"]);
    if (m.ui.wizard.length > 0)
      for (const name of Object.keys(inputs))
        if (!wizard.has(name) && !connectInputs.has(name))
          issue(`input ${name} is in no wizard step`, ["ui", "wizard"]);
    if (m.lifecycle.driver !== "bundle")
      for (const action of m.actions)
        if (action.effect.kind === "operation")
          issue(`action ${action.id}: operations need the bundle driver`, ["actions"]);
    for (const w of m.ui.status)
      for (const id of w.actions ?? [])
        if (!ids.includes(id)) issue(`status widget names unknown action ${id}`, ["ui", "status"]);
    if (!m.ownership.allowed.includes(m.ownership.default))
      issue("the default ownership must be allowed", ["ownership"]);
    const inVm = m.placement === "customer-vm" || m.placement === "customer-appliance";
    if (inVm && m.lifecycle.driver !== "bundle" && m.lifecycle.driver !== "compose")
      issue("packages in a VM use the bundle or compose driver", ["lifecycle", "driver"]);
    if (inVm && !(m.requires.autopilotRole && roleHas(m.requires.autopilotRole, "apps")))
      issue("packages in a VM require an Autopilot role with apps (agent-apps)", ["requires"]);
    const customerPaths = m.lifecycle.customerFiles.map((f) => f.path);
    if (new Set(customerPaths).size !== customerPaths.length)
      issue("a customer file is named twice", ["lifecycle", "customerFiles"]);
    if (customerPaths.length > 0 && m.lifecycle.driver !== "bundle")
      issue("only the bundle driver changes customer files", ["lifecycle", "customerFiles"]);
    for (const file of m.lifecycle.customerFiles)
      if (file.when !== undefined && inputs[file.when]?.type !== "boolean")
        issue(`customer file ${file.path}: when names no toggle input`, [
          "lifecycle",
          "customerFiles",
        ]);
    if (m.placement === "provider" && m.lifecycle.driver !== "provider-api")
      issue("provider packages use the provider-api driver", ["lifecycle", "driver"]);
    if (m.ui.component && !m.publisher.firstParty)
      issue("only first-party packages may bring a component", ["ui", "component"]);
    // Resources (docs/packages.md "Resources"): servers only for appliances, bound to real inputs.
    // With modes, a package is an appliance in the modes whose placement says so.
    const placements = [
      { placement: m.placement, ownership: m.ownership },
      ...Object.values(m.modes?.values ?? {}),
    ];
    const appliance = placements.filter((p) => p.placement === "customer-appliance");
    const servers = serverResources(m);
    const keys = servers.map((s) => s.key);
    if (new Set(keys).size !== keys.length) issue("resource keys must be unique", ["resources"]);
    if (appliance.length > 0) {
      if (servers.length === 0)
        issue("an appliance package declares the server it runs on", ["resources"]);
      if (appliance.some((p) => p.ownership.default !== "managed"))
        issue("an appliance is managed by default (ADR 0002 A)", ["ownership"]);
    } else if (servers.length > 0) {
      issue("only appliance packages create servers", ["resources"]);
    }
    const bound = (
      binding: unknown,
      where: (string | number)[],
      widgets?: readonly InputWidget[],
    ) => {
      if (typeof binding !== "object" || binding === null) return;
      const name =
        "input" in binding ? binding.input : "network" in binding ? binding.network : undefined;
      if (typeof name !== "string") return;
      const input = inputs[name];
      if (!input) issue(`resource names unknown input ${name}`, where);
      else if (widgets && !widgets.includes(input["x-ui"].widget))
        issue(`resource input ${name} must use the ${widgets.join(" or ")} widget`, where);
    };
    const networkWidgets: readonly InputWidget[] = ["network", "cidr-list"];
    servers.forEach((s, i) => {
      bound(s.serverType, ["resources", i, "serverType"]);
      bound(s.image, ["resources", i, "image"]);
      bound(
        s.location,
        ["resources", i, "location"],
        typeof s.location === "object" && "network" in s.location ? networkWidgets : undefined,
      );
      if (s.networks) bound(s.networks, ["resources", i, "networks"], networkWidgets);
      if (s.networkRoutes.length > 0 && !s.networks)
        issue("network routes need the networks the server joins", ["resources", i]);
      if (Object.keys(s.labels).some((k) => k.startsWith("cloudserver")))
        issue("cloudserver-* labels are the panel's own", ["resources", i, "labels"]);
      // Path A reaches the helper over SSH at a public address (docs/packages.md "Resources").
      if (!s.publicIpv4 && !s.publicIpv6)
        issue("an appliance needs a public address for its helper", ["resources", i]);
    });
    if (m.modes) {
      const select = inputs[m.modes.input];
      const values = Object.keys(m.modes.values).sort();
      if (select?.["x-ui"].widget !== "select" || select.type !== "string")
        issue("modes.input must name a select input", ["modes", "input"]);
      else if ([...(select.enum ?? [])].sort().join(",") !== values.join(","))
        issue("modes.values must be the values of its input", ["modes", "values"]);
      const first = select?.default !== undefined ? m.modes.values[String(select.default)] : null;
      if (!first) issue("the modes' input needs a default that is a mode", ["modes", "input"]);
      else if (
        first.placement !== m.placement ||
        first.ownership.default !== m.ownership.default ||
        first.ownership.allowed.join(",") !== m.ownership.allowed.join(",")
      )
        issue("placement and ownership must be those of the default mode", ["modes"]);
      for (const [value, mode] of Object.entries(m.modes.values)) {
        if (!mode.ownership.allowed.includes(mode.ownership.default))
          issue(`mode ${value}: the default ownership must be allowed`, ["modes", value]);
        if (mode.placement === "provider" || mode.placement === "hybrid")
          issue(`mode ${value}: only VM placements have modes yet`, ["modes", value]);
      }
    }
  });
export type PackageManifest = z.infer<typeof PackageManifest>;
export type PackageManifestInput = z.input<typeof PackageManifest>;

/** Where an instance with these inputs runs and who may own it (its mode, else the manifest's). */
export function modeOf(
  manifest: Pick<PackageManifest, "placement" | "ownership" | "modes" | "inputs">,
  inputs: Record<string, unknown>,
): { mode: string | null; placement: PackagePlacement; ownership: PackageManifest["ownership"] } {
  const modes = manifest.modes;
  if (!modes) return { mode: null, placement: manifest.placement, ownership: manifest.ownership };
  const value = inputs[modes.input] ?? manifest.inputs.properties[modes.input]?.default;
  const mode = typeof value === "string" ? modes.values[value] : undefined;
  return mode
    ? { mode: String(value), placement: mode.placement, ownership: mode.ownership }
    : { mode: null, placement: manifest.placement, ownership: manifest.ownership };
}

/** Why a package of the catalogue cannot be installed here. */
export const PackageUnavailable = z.enum([
  "bundle_missing",
  "bundle_unverified",
  "no_provider_plane",
  "driver_not_built",
  /** An appliance package: its server enrols Autopilot, and no hub is configured. */
  "no_autopilot",
]);
export type PackageUnavailable = z.infer<typeof PackageUnavailable>;

/** `GET /projects/:projectId/packages`: the catalogue of package types. */
export const PackageCatalogueEntry = z.object({
  manifest: PackageManifest,
  available: z.boolean(),
  unavailable: PackageUnavailable.nullable(),
  /** Instances of it in this project. */
  installed: z.number().int(),
});
export type PackageCatalogueEntry = z.infer<typeof PackageCatalogueEntry>;
export const PackageCatalogue = z.object({ packages: z.array(PackageCatalogueEntry) });
export type PackageCatalogue = z.infer<typeof PackageCatalogue>;

export const PACKAGE_PHASES = [
  "pending",
  "applying",
  "ready",
  "drift",
  "error",
  "removing",
] as const;
export const PackagePhase = z.enum(PACKAGE_PHASES);
export type PackagePhase = z.infer<typeof PackagePhase>;

export const PackageCondition = z.object({
  type: z.enum(["ready", "drift", "error"]),
  status: z.enum(["true", "false", "unknown"]),
  /** A stable key the panel translates (`applied`, `manual_edit`, `verify_failed`, …). */
  reason: z.string().regex(/^[a-z_]{1,48}$/),
  message: z.string().max(500),
  at: z.iso.datetime(),
});
export type PackageCondition = z.infer<typeof PackageCondition>;

/**
 * Who did something: a person (panel), a key, the assistant, Autopilot,
 * someone over SSH (drift), or someone at the provider (an appliance's
 * labels, protection or type changed outside the panel).
 */
export const PackageActor = z.object({
  kind: z.enum(["person", "api_key", "assistant", "autopilot", "ssh", "provider"]),
  id: z.string().nullable(),
  name: z.string().nullable(),
});
export type PackageActor = z.infer<typeof PackageActor>;

export const FileState = z.object({
  path: z.string().max(300),
  state: z.enum(["ok", "drift", "missing", "unknown"]),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
});
export type FileState = z.infer<typeof FileState>;

/**
 * A customer's file the instance changes (docs/packages.md, "Customer
 * files"), as the helper last reported it:
 * - `pending` — our change is not in the file: a card shows the diff;
 * - `ok` — the file holds our change (adopted: our keys are as we set them);
 * - `changed` — not adopted, edited since we changed it: the customer's
 *   state, shown, never reverted;
 * - `drift` — adopted, and our keys were edited by hand;
 * - `missing` — the file is gone;
 * - `unsupported` — a link, over 64 KiB, or not what the edit needs (not
 *   a JSON object): only a person can change it;
 * - `off` — the input that turns the change on (`when`) is off: the file is
 *   left as it is, what we wrote earlier stays;
 * - `unknown` — not checked yet.
 */
export const CustomerFileState = z.object({
  path: z.string().max(300),
  format: CustomerFileFormat,
  state: z.enum(["pending", "ok", "changed", "drift", "missing", "unsupported", "off", "unknown"]),
  /** Handed over to the panel («Передать файл под управление панели»): co-managed from then on. */
  adopted: z.boolean(),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  /** What we last wrote (or found already right): later edits are measured against it. */
  lastApplied: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
});
export type CustomerFileState = z.infer<typeof CustomerFileState>;

/** A check of a bundle's `drift` script that is not a file: an engine changed by hand, a service turned off. */
export const PackageCheck = z.object({
  /** A stable key the panel translates (`package_version`, `service_disabled`, …). */
  name: z.string().regex(/^[a-z_]{1,32}$/),
  state: z.enum(["ok", "drift"]),
});
export type PackageCheck = z.infer<typeof PackageCheck>;

/**
 * Where a resource of an instance is (docs/packages.md "Resources"):
 * `planned` → `creating` (the provider's create runs) → `protecting`
 * (delete and rebuild protection) → `enrolling` (waiting for the helper
 * over SSH) → `ready`; removing or rolling back: `unprotecting` →
 * `deleting` → `deleted`; `failed` stops the install and rolls back what
 * exists; `released`: taken over by the customer («Забрать себе»).
 */
export const RESOURCE_STATES = [
  "planned",
  "creating",
  "protecting",
  "enrolling",
  "ready",
  "unprotecting",
  "deleting",
  "deleted",
  "failed",
  "released",
] as const;
export const ResourceState = z.enum(RESOURCE_STATES);
export type ResourceState = z.infer<typeof ResourceState>;

/** A change made to an appliance outside the panel (ADR 0002 A: detected, not prevented). */
export const ApplianceDrift = z.object({
  check: z.enum(["label", "protection", "type"]),
  /** What we set (`cloudserver-managed=true`, `delete`, `cx23`) and what is there now. */
  expected: z.string(),
  actual: z.string().nullable(),
});
export type ApplianceDrift = z.infer<typeof ApplianceDrift>;

/** One resource an instance created (or plans, or created and removed). */
export const PackageResourceState = z.object({
  key: z.string(),
  kind: z.enum(["server", "firewall"]),
  /** The provider-side name (`hello-appliance-1a2b3c4d-vm`). */
  name: z.string(),
  /** Our id in the resource copy while it exists. */
  resourceId: z.uuid().nullable(),
  state: ResourceState,
  serverType: z.string().nullable(),
  location: z.string().nullable(),
  image: z.string().nullable(),
  /** EUR per hour and per month from the catalogue (server and its primary IPv4); null: not priced. */
  hourly: z.string().nullable(),
  monthly: z.string().nullable(),
  /** Why it failed or why it was rolled back (a stable reason). */
  error: z.string().nullable(),
  drift: z.array(ApplianceDrift),
  since: z.iso.datetime(),
});
export type PackageResourceState = z.infer<typeof PackageResourceState>;

export const PackageInstance = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  packageId: PackageId,
  packageVersion: PackageVersion,
  /** The server it runs on (`customer-vm`); null in the provider plane. */
  serverId: z.uuid().nullable(),
  serverName: z.string().nullable(),
  /** The provider plane's service (`provider`). */
  planeServiceId: z.string().nullable(),
  /** Where it runs: the manifest's placement, or its mode's (`modes`). */
  placement: PackagePlacement,
  /** How it was connected (the wizard's choice; `connectionId`: the organisation's OAuth connection). */
  connect: z
    .object({
      method: z.enum(["none", "secret", "oauth", "device-login"]),
      connectionId: z.uuid().nullable(),
    })
    .nullable(),
  ownership: Ownership,
  autonomy: z.object({
    level: AutonomyLevel,
    /** Where the level comes from. */
    source: z.enum(["instance", "project", "organization", "package"]),
  }),
  /** «Руки прочь» on the instance; `serverFrozen` on its server. */
  frozen: z.boolean(),
  serverFrozen: z.boolean(),
  phase: PackagePhase,
  conditions: z.array(PackageCondition),
  desired: z.object({
    revision: z.number().int(),
    /** Secret inputs are never sent back: `secretsSet` names those that hold a value. */
    inputs: InputValues,
    secretsSet: z.array(Name),
  }),
  lastApplied: z
    .object({
      revision: z.number().int(),
      at: z.iso.datetime(),
      files: z.array(z.object({ path: z.string(), sha256: z.string() })),
    })
    .nullable(),
  actual: z
    .object({
      checkedAt: z.iso.datetime(),
      files: z.array(FileState),
      /** Checks of the drift script that are not files (the ones that drifted). */
      checks: z.array(PackageCheck).optional(),
      status: z.record(z.string(), z.unknown()).nullable(),
    })
    .nullable(),
  outputs: z.array(z.object({ name: Name, value: z.string() })),
  /** The customer's files the bundle changes, with their state (empty for most packages). */
  customerFiles: z.array(CustomerFileState),
  /** EUR per month from the manifest's billing (0 while packages have no prices). */
  monthly: z.string(),
  /** The provider resources it created (appliances), with their state; empty for most packages. */
  resources: z.array(PackageResourceState),
  /** EUR per month its resources cost at the provider (the cost ledger has them under the instance). */
  resourcesMonthly: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PackageInstance = z.infer<typeof PackageInstance>;

/** `GET /projects/:projectId/appliances`: every managed appliance of the project. */
export const ManagedAppliance = z.object({
  serverId: z.uuid(),
  serverName: z.string(),
  instanceId: z.uuid(),
  packageId: PackageId,
  key: z.string(),
  state: ResourceState,
  drift: z.array(ApplianceDrift),
});
export type ManagedAppliance = z.infer<typeof ManagedAppliance>;
export const ManagedApplianceList = z.object({ appliances: z.array(ManagedAppliance) });
export type ManagedApplianceList = z.infer<typeof ManagedApplianceList>;

/** `POST /projects/:projectId/packages/<id>/plan`: what an install would create, and its price. */
export const PlanPackageRequest = z.object({ inputs: InputValues.default({}) });
export type PlanPackageRequest = z.input<typeof PlanPackageRequest>;

export const PlannedResource = z.object({
  key: z.string(),
  kind: z.enum(["server", "firewall"]),
  title: Localized,
  name: z.string(),
  serverType: z.string().nullable(),
  location: z.string().nullable(),
  image: z.string().nullable(),
  networks: z.array(z.object({ id: z.uuid(), name: z.string() })),
  publicIpv4: z.boolean(),
  hourly: z.string().nullable(),
  monthly: z.string().nullable(),
});
export type PlannedResource = z.infer<typeof PlannedResource>;

export const PackagePlan = z.object({
  resources: z.array(PlannedResource),
  currency: z.string().length(3),
  /** The package's own price (0 while packages have no prices) and its resources', per month. */
  monthly: z.string(),
  resourcesMonthly: z.string(),
  resourcesHourly: z.string(),
  /** Part of it has no price in the catalogue (counted as zero). */
  unpriced: z.boolean(),
  /**
   * What the budget guard is given (`POST …/costs/preview` answers its
   * verdict): the servers as `package.install`'s `servers`.
   */
  servers: z.array(
    z.object({ serverType: z.string(), region: z.string(), publicIpv4: z.boolean() }),
  ),
});
export type PackagePlan = z.infer<typeof PackagePlan>;

/**
 * «Забрать себе» (ADR 0002 A): the person's SSH key goes onto the appliance
 * through the agent, our labels and protection come off, and the instance
 * no longer manages it — support for it ends.
 */
export const TakeOverRequest = z.object({ sshKeyId: z.uuid() });
export type TakeOverRequest = z.infer<typeof TakeOverRequest>;

export const REVISION_REASONS = ["install", "configure", "action", "rollback", "revert"] as const;
export const PackageRevision = z.object({
  number: z.number().int(),
  inputs: InputValues,
  reason: z.enum(REVISION_REASONS),
  /** The action of an `action` revision; the revision rolled back to for `rollback`. */
  detail: z.string().nullable(),
  state: z.enum(["pending", "applied", "failed", "rolled_back", "superseded", "proposed"]),
  actor: PackageActor,
  createdAt: z.iso.datetime(),
  appliedAt: z.iso.datetime().nullable(),
  message: z.string().nullable(),
});
export type PackageRevision = z.infer<typeof PackageRevision>;

export const JOURNAL_VERBS = [
  "install",
  "configure",
  "action",
  "rollback",
  "revert",
  "remove",
  "check",
  "drift",
  "freeze",
  "unfreeze",
  "autonomy",
  "proposal",
  "approve",
  "reject",
  "patch",
  "adopt",
  "release",
  // Resources (docs/packages.md "Resources"): created, removed, taken over, drift put back.
  "provision",
  "deprovision",
  "take_over",
  "restore",
] as const;
export const PackageJournalEntry = z.object({
  id: z.uuid(),
  at: z.iso.datetime(),
  actor: PackageActor,
  verb: z.enum(JOURNAL_VERBS),
  revision: z.number().int().nullable(),
  outcome: z.enum(["success", "failed", "refused", "pending"]),
  /** A stable reason key (`manual_edit`, `presence`, `verify_failed`, …) or null. */
  reason: z.string().nullable(),
  /** Paths, revision numbers, levels — never secret values. */
  detail: z.record(z.string(), z.unknown()),
});
export type PackageJournalEntry = z.infer<typeof PackageJournalEntry>;

/**
 * Why an Autopilot write waits for a person (ADR 0002 C.3, C.6, B);
 * `customer_file`: the write changes a file of the customer's (decision 4
 * of 2026-10-22).
 */
export const ProposalReason = z.enum(["presence", "autonomy", "ownership", "customer_file"]);
export type ProposalReason = z.infer<typeof ProposalReason>;

/**
 * The exact change of a customer's file a `patch` card makes: the file as
 * it is and as it would be (secrets hidden; null when too big to show),
 * and the SHA-256 of both — the helper refuses when the file is no longer
 * the one the card was made from.
 */
export const ProposalPatch = z.object({
  path: z.string().max(300),
  format: CustomerFileFormat,
  baseSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  resultSha256: z.string().regex(/^[0-9a-f]{64}$/),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type ProposalPatch = z.infer<typeof ProposalPatch>;

/** An Autopilot write as a card in the panel: it runs only when a person approves it. */
export const PackageProposal = z.object({
  id: z.uuid(),
  instanceId: z.uuid(),
  /**
   * `revert`: write our files back over a manual edit; `apply`: apply the
   * desired revision; `patch`: change a customer's file (`patch` says how).
   */
  verb: z.enum(["revert", "apply", "patch"]),
  revision: z.number().int(),
  reason: ProposalReason,
  /** Files the write would touch, with what is there now. */
  files: z.array(FileState),
  patch: ProposalPatch.nullable(),
  state: z.enum(["open", "approved", "rejected", "superseded"]),
  createdAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  decidedBy: z.string().nullable(),
});
export type PackageProposal = z.infer<typeof PackageProposal>;

/** «Автопилот меняет …»: one change at a time per server. */
export const PackageLease = z.object({
  serverId: z.uuid(),
  instanceId: z.uuid(),
  packageId: PackageId,
  verb: z.string(),
  holder: PackageActor,
  startedAt: z.iso.datetime(),
  until: z.iso.datetime(),
});
export type PackageLease = z.infer<typeof PackageLease>;

/** `GET /projects/:projectId/package-instances/:instanceId`. */
export const PackageInstanceDetail = z.object({
  instance: PackageInstance,
  revisions: z.array(PackageRevision),
  journal: z.array(PackageJournalEntry),
  proposals: z.array(PackageProposal),
  lease: PackageLease.nullable(),
  /** People with a terminal session open on the instance's server now. */
  presence: z.number().int(),
});
export type PackageInstanceDetail = z.infer<typeof PackageInstanceDetail>;

export const PackageInstanceList = z.object({ instances: z.array(PackageInstance) });
export type PackageInstanceList = z.infer<typeof PackageInstanceList>;

/** `GET /projects/:projectId/servers/:serverId/packages`: the server tab. */
export const ServerPackages = z.object({
  instances: z.array(PackageInstance),
  frozen: z.boolean(),
  lease: PackageLease.nullable(),
  presence: z.number().int(),
  /** The server's Autopilot role; packages in a VM need the capability `apps`. */
  role: AgentRole.nullable(),
});
export type ServerPackages = z.infer<typeof ServerPackages>;

/**
 * The connect method chosen in the wizard (docs/packages.md, "Connect
 * flows"); `connectionId` names the organisation's connection for `oauth`.
 * Left out: the manifest's only method.
 */
export const PackageConnectChoice = z.object({
  method: z.enum(["none", "secret", "oauth", "device-login"]),
  connectionId: z.uuid().optional(),
});
export type PackageConnectChoice = z.infer<typeof PackageConnectChoice>;

export const InstallPackageRequest = z.object({
  serverId: z.uuid().optional(),
  inputs: InputValues.default({}),
  ownership: Ownership.optional(),
  connect: PackageConnectChoice.optional(),
});
export type InstallPackageRequest = z.input<typeof InstallPackageRequest>;

/**
 * An organisation's reusable connection to a third party (docs/packages.md,
 * "Connect flows"): OAuth client credentials (a Tailscale OAuth client), kept
 * encrypted and never sent back; the client id only as its first characters.
 */
export const PackageConnection = z.object({
  id: z.uuid(),
  provider: ConnectProvider,
  name: z.string(),
  /** The first characters of the client id, to tell clients apart. */
  clientIdHint: z.string(),
  /** Scopes the provider reported for the credentials, when it does. */
  scopes: z.array(z.string()),
  createdAt: z.iso.datetime(),
  createdBy: z.string().nullable(),
  lastUsedAt: z.iso.datetime().nullable(),
  /** The last failure to use it (a revoked client), else null. */
  lastError: z.string().nullable(),
});
export type PackageConnection = z.infer<typeof PackageConnection>;

export const PackageConnectionList = z.object({ connections: z.array(PackageConnection) });
export type PackageConnectionList = z.infer<typeof PackageConnectionList>;

export const CreatePackageConnectionRequest = z.object({
  provider: ConnectProvider,
  name: z.string().trim().min(1).max(64),
  clientId: z
    .string()
    .trim()
    .min(4)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/),
  clientSecret: z
    .string()
    .trim()
    .min(8)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/),
});
export type CreatePackageConnectionRequest = z.infer<typeof CreatePackageConnectionRequest>;

/** A device of the connection's network as the provider lists it (Tailscale's devices API). */
export const ConnectionDevice = z.object({
  id: z.string(),
  name: z.string(),
  hostname: z.string(),
  addresses: z.array(z.string()),
  os: z.string(),
  lastSeen: z.string().nullable(),
  online: z.boolean().nullable(),
  tags: z.array(z.string()),
  advertisedRoutes: z.array(z.string()),
  enabledRoutes: z.array(z.string()),
  /** When the node's key expires; null when key expiry is off. */
  keyExpiry: z.string().nullable(),
});
export type ConnectionDevice = z.infer<typeof ConnectionDevice>;

/** `GET …/package-instances/:id/connection/devices`: the tailnet as the instance's connection sees it. */
export const ConnectionDevices = z.object({
  devices: z.array(ConnectionDevice),
  /** The instance's own device in the list, when found (its hostname or Tailscale IP). */
  self: z.string().nullable(),
});
export type ConnectionDevices = z.infer<typeof ConnectionDevices>;

/** New inputs (all of them; a secret left out keeps its value), and the ownership. */
export const ConfigurePackageRequest = z.object({
  inputs: InputValues,
  ownership: Ownership.optional(),
});
export type ConfigurePackageRequest = z.input<typeof ConfigurePackageRequest>;

export const RollbackPackageRequest = z.object({ revision: z.number().int().min(1) });
export type RollbackPackageRequest = z.infer<typeof RollbackPackageRequest>;

export const PackageActionRequest = z.object({ params: InputValues.default({}) });
export type PackageActionRequest = z.input<typeof PackageActionRequest>;

/** «Руки прочь» on an instance or a whole server: Autopilot writes nothing there. */
export const FreezeRequest = z.object({ frozen: z.boolean() });
export type FreezeRequest = z.infer<typeof FreezeRequest>;

/**
 * `PUT …/package-instances/:id/customer-files`: hand a customer's file over
 * to the panel (`adopted: true`, co-managed from then on) or give it back.
 */
export const AdoptCustomerFileRequest = z.object({
  path: z.string().min(1).max(300),
  adopted: z.boolean(),
});
export type AdoptCustomerFileRequest = z.infer<typeof AdoptCustomerFileRequest>;

/**
 * `POST …/package-proposals/:id/approve`: for a card of a customer's file
 * whose change needs a restart (`restart` in the manifest), whether the
 * bundle's restart step runs after the change (default: no — the change
 * takes effect at the service's next start).
 */
export const DecideProposalRequest = z.object({ restart: z.boolean().optional() });
export type DecideProposalRequest = z.infer<typeof DecideProposalRequest>;

/** An instance's, project's or organisation's autonomy (null: the next level up). */
export const PackageAutonomySetting = z.object({ level: AutonomyLevel.nullable() });
export type PackageAutonomySetting = z.infer<typeof PackageAutonomySetting>;

/** `GET /projects/:projectId/packages/autonomy`. */
export const PackageAutonomyState = z.object({
  organization: AutonomyLevel.nullable(),
  project: AutonomyLevel.nullable(),
});
export type PackageAutonomyState = z.infer<typeof PackageAutonomyState>;

/**
 * `DELETE …/instances/:instanceId?overwrite=true`: remove files edited by
 * hand too; `data=delete`: also the data the app keeps (only for packages
 * whose uninstall data policy is `ask`; `keep` never deletes it).
 */
export const RemovePackageQuery = z.object({
  overwrite: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
  data: z.enum(["keep", "delete"]).default("keep"),
});

/** What a write did: the instance after it, and the revision it made. */
export const PackageChangeResult = z.object({
  instance: PackageInstance,
  revision: PackageRevision.nullable(),
  /** Set when the change stopped: a manual edit, a failed check rolled back, a refusal. */
  stopped: z
    .object({
      reason: z.string(),
      files: z.array(FileState),
      checks: z.array(PackageCheck).optional(),
    })
    .nullable(),
  /** What an `operation` action printed (a JSON object; texts redacted), else null. */
  output: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type PackageChangeResult = z.infer<typeof PackageChangeResult>;
